import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SearchableMultiSelect from "@/components/form/SearchableMultiSelect";
import RichTextEditor from "@/components/form/RichTextEditor";
import NextOfKinFields from "@/components/form/NextOfKinFields";
import CarePlanAiAssist from "@/components/CarePlanAiAssist";
import CarePlanReviewHistory from "@/components/CarePlanReviewHistory";
import CareNoteAssistant from "@/components/CareNoteAssistant";
import VoiceCareRecorder from "@/components/VoiceCareRecorder";
import FileAttachments from "@/components/FileAttachments";
import {
  PRIMARY_NEEDS_OPTIONS,
  ALLERGY_OPTIONS,
  asArray,
} from "@/lib/clientFields";
import {
  CARE_PLAN_TYPE_GROUPS,
  ALL_CARE_PLAN_TYPES,
  carePlanTypeSideEffect,
} from "@/lib/carePlanConfig";

// Groups registry — field.groupsFrom → grouped options list.
const GROUP_REGISTRY = {
  carePlanTypes: CARE_PLAN_TYPE_GROUPS,
};

// Side-effect registry — field.sideEffect → (newValue, values) => partialValues
const SIDE_EFFECTS = {
  carePlanTypeSideEffect,
};

// Field types that always take the full row width.
const FULL_WIDTH_TYPES = new Set([
  "textarea",
  "richtext",
  "primary_needs_multiselect",
  "allergy_multiselect",
  "next_of_kin",
  "grouped_select",
  "site_select",
]);

function humanDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function RecordMetaHeader({ record }) {
  if (!record) return null;
  const version = record.version || 1;
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-300 mb-2"
      data-testid="record-meta-header"
    >
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">Version</span>
        <span className="font-semibold text-slate-800 dark:text-slate-100">v{version}</span>
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">Created</span>
        <span className="tabular-nums">{humanDate(record.created_at)}</span>
        {record.created_by_name && <span className="text-slate-400">· {record.created_by_name}</span>}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">Last Updated</span>
        <span className="tabular-nums">{humanDate(record.updated_at)}</span>
      </span>
    </div>
  );
}

export default function RecordDialog({ open, onOpenChange, module, record, onSubmit, clientId }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isCarePlan = module.key === "care-planning";
  const isDailyLog = module.key === "daily-logs";

  useEffect(() => {
    if (!open) return;
    const defaults = {};
    module.fields.forEach((f) => {
      if (f.type === "next_of_kin") {
        defaults.next_of_kin_name = record?.next_of_kin_name ?? (typeof record?.next_of_kin === "string" ? record.next_of_kin : "");
        defaults.next_of_kin_relationship = record?.next_of_kin_relationship ?? "";
        defaults.next_of_kin_phone = record?.next_of_kin_phone ?? "";
        defaults.next_of_kin_email = record?.next_of_kin_email ?? "";
        return;
      }
      if (f.type === "primary_needs_multiselect" || f.type === "allergy_multiselect" || f.type === "multiselect") {
        defaults[f.name] = asArray(record?.[f.name]);
        return;
      }
      defaults[f.name] = record?.[f.name] ?? "";
    });
    setValues(defaults);
  }, [open, record, module]);

  const change = (name, v, field) => {
    setValues((s) => {
      let next = { ...s, [name]: v };
      if (field?.sideEffect && SIDE_EFFECTS[field.sideEffect]) {
        const patch = SIDE_EFFECTS[field.sideEffect](v, next) || {};
        next = { ...next, ...patch };
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of module.fields) {
      if (f.type === "next_of_kin") continue;
      const val = values[f.name];
      const empty = Array.isArray(val) ? val.length === 0 : !String(val ?? "").trim();
      if (f.required && empty) return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-white dark:bg-slate-900 max-h-[90vh] overflow-y-auto"
        data-testid="record-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading dark:text-slate-100">{record ? "Edit" : "New"} — {module.label}</DialogTitle>
          <DialogDescription className="sr-only">
            {record ? "Edit an existing" : "Create a new"} {module.label} record.
          </DialogDescription>
        </DialogHeader>

        {module.showRecordMeta && record && <RecordMetaHeader record={record} />}

        {isDailyLog && (
          <div className="space-y-3 mb-2" data-testid="daily-log-toolbar">
            <div className="flex items-center gap-2 flex-wrap">
              <CareNoteAssistant
                initialNote={values.notes || ""}
                clientId={clientId}
                onApply={(rewritten) => {
                  const asPlain = rewritten
                    .replace(/<br\s*\/?>/gi, "\n")
                    .replace(/<\/(p|div|h[1-6]|li)>/gi, "\n")
                    .replace(/<[^>]+>/g, "")
                    .replace(/\n{3,}/g, "\n\n")
                    .trim();
                  setValues((s) => ({ ...s, notes: asPlain }));
                }}
              />
            </div>
            <VoiceCareRecorder
              clientId={clientId}
              relatedId={record?.id}
              onApply={(rewritten) => {
                const asPlain = rewritten
                  .replace(/<br\s*\/?>/gi, "\n")
                  .replace(/<\/(p|div|h[1-6]|li)>/gi, "\n")
                  .replace(/<[^>]+>/g, "")
                  .replace(/\n{3,}/g, "\n\n")
                  .trim();
                setValues((s) => ({ ...s, notes: asPlain }));
              }}
            />
          </div>
        )}

        {isCarePlan && clientId && (
          <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="care-plan-toolbar">
            <LoadTemplateBodyButton
              planType={values.plan_type}
              onLoad={(body) => {
                setValues((s) => {
                  const next = { ...s };
                  Object.entries(body).forEach(([k, v]) => {
                    // Only fill blank fields to avoid overwriting user work
                    if (!next[k] || (typeof next[k] === "string" && next[k].trim() === "")) {
                      next[k] = v;
                    }
                  });
                  return next;
                });
                toast.success("Template body applied to empty fields");
              }}
            />
            <CarePlanAiAssist
              clientId={clientId}
              planType={values.plan_type}
              onApply={(draft) => {
                setValues((s) => ({ ...s, ...draft }));
              }}
            />
          </div>
        )}

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          {module.fields.map((f) => {
            const testid = `field-${module.key}-${f.name}`;
            const full = FULL_WIDTH_TYPES.has(f.type);

            if (f.type === "next_of_kin") {
              return (
                <NextOfKinFields
                  key={f.name}
                  values={values}
                  onChange={(k, v) => change(k, v)}
                  testid={`field-${module.key}-nok`}
                />
              );
            }

            return (
              <div key={f.name} className={`space-y-1 ${full ? "md:col-span-2" : ""}`}>
                <Label htmlFor={testid} className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>

                {f.type === "textarea" ? (
                  <Textarea
                    id={testid} data-testid={testid} rows={3}
                    value={values[f.name] || ""}
                    onChange={(e) => change(f.name, e.target.value, f)}
                    className="text-sm bg-white dark:bg-slate-900"
                  />
                ) : f.type === "richtext" ? (
                  <RichTextEditor
                    value={values[f.name] || ""}
                    onChange={(v) => change(f.name, v, f)}
                    testid={testid}
                    placeholder={f.placeholder || "Type here. Use the toolbar for headings, lists, and formatting…"}
                  />
                ) : f.type === "primary_needs_multiselect" ? (
                  <SearchableMultiSelect
                    value={values[f.name] || []}
                    onChange={(v) => change(f.name, v, f)}
                    options={PRIMARY_NEEDS_OPTIONS}
                    placeholder="Search or add a care need…"
                    allowCustom
                    testid={testid}
                  />
                ) : f.type === "allergy_multiselect" ? (
                  <SearchableMultiSelect
                    value={values[f.name] || []}
                    onChange={(v) => change(f.name, v, f)}
                    options={ALLERGY_OPTIONS}
                    placeholder="Search or add an allergy/warning…"
                    allowCustom
                    variant="allergy"
                    testid={testid}
                  />
                ) : f.type === "grouped_select" ? (
                  <GroupedSelect
                    testid={testid}
                    value={values[f.name] || ""}
                    onChange={(v) => change(f.name, v, f)}
                    groups={GROUP_REGISTRY[f.groupsFrom] || []}
                    placeholder="Select…"
                  />
                ) : f.type === "site_select" ? (
                  <SiteSelect
                    testid={testid}
                    value={values[f.name] || ""}
                    onChange={(v) => change(f.name, v, f)}
                  />
                ) : f.type === "select" ? (
                  <Select value={values[f.name] || ""} onValueChange={(v) => change(f.name, v, f)}>
                    <SelectTrigger id={testid} data-testid={testid} className="text-sm h-9 bg-white dark:bg-slate-900">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900">
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`${testid}-opt-${o}`}>
                          {o.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "multiselect" ? (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid={testid}>
                    {f.options.map((o) => {
                      const arr = Array.isArray(values[f.name]) ? values[f.name] : [];
                      const on = arr.includes(o);
                      return (
                        <button
                          key={o} type="button"
                          data-testid={`${testid}-opt-${o}`}
                          onClick={() => change(f.name, on ? arr.filter((x) => x !== o) : [...arr, o], f)}
                          className={`px-2.5 py-1 text-xs rounded-sm border font-medium transition-colors ${
                            on
                              ? "bg-sky-700 text-white border-sky-700"
                              : "bg-white border-slate-300 text-slate-700 hover:border-sky-500"
                          }`}
                        >{o}</button>
                      );
                    })}
                  </div>
                ) : (
                  <Input
                    id={testid} data-testid={testid} type={f.type || "text"}
                    value={values[f.name] || ""}
                    onChange={(e) => change(f.name, e.target.value, f)}
                    className="text-sm h-9 bg-white dark:bg-slate-900"
                  />
                )}
              </div>
            );
          })}
          <DialogFooter className="md:col-span-2 mt-2 flex-col sm:flex-row gap-2">
            <Button
              type="button" variant="outline" onClick={() => onOpenChange(false)}
              data-testid="record-dialog-cancel"
              className="h-11 md:h-10"
            >Cancel</Button>
            <Button
              type="submit" disabled={submitting}
              data-testid="record-dialog-submit"
              className="bg-sky-700 hover:bg-sky-800 h-11 md:h-10"
            >
              {submitting ? "Saving…" : record ? "Update Record" : "Create Record"}
            </Button>
          </DialogFooter>
        </form>

        {isCarePlan && record?.id && (
          <div className="mt-4 space-y-4">
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Attachments
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                Attach Behaviour Support Plans, Risk Assessments, Hospital Letters, MCA/DoLS forms, Consent forms, PBS plans, Clinical letters. Files are stored securely in your Ayobell workspace.
              </div>
              <FileAttachments module="care-planning" recordId={record.id} />
            </div>
            <CarePlanReviewHistory planId={record.id} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LoadTemplateBodyButton({ planType, onLoad }) {
  const [busy, setBusy] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!planType) { setAvailable(false); return; }
    let alive = true;
    api.get(`/care-plan-wave2/template-body?plan_type=${encodeURIComponent(planType)}`)
      .then((r) => { if (alive) setAvailable(!!r.data?.available); })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, [planType]);

  if (!available) return null;

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/care-plan-wave2/template-body?plan_type=${encodeURIComponent(planType)}`);
      if (r.data?.body) onLoad(r.data.body);
    } catch { toast.error("Failed to load template"); }
    finally { setBusy(false); }
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={load}
      disabled={busy}
      data-testid="btn-load-template-body"
      className="h-8 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
    >
      <FileText className="w-3.5 h-3.5 mr-1.5" />
      {busy ? "Loading…" : `Load ${planType} template`}
    </Button>
  );
}

/** Native <select> with <optgroup>s — Fluent-native, accessible, no popover needed. */
function GroupedSelect({ value, onChange, groups, placeholder, testid }) {
  // If the current value isn't in any group (legacy value), show it as a "(Legacy)" option so it renders.
  const known = groups.some((g) => g.options.includes(value));
  return (
    <select
      id={testid}
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
    >
      <option value="">{placeholder}</option>
      {value && !known && (
        <optgroup label="Legacy">
          <option value={value}>{value} (legacy)</option>
        </optgroup>
      )}
      {groups.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o} value={o} data-testid={`${testid}-opt-${o}`}>{o}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// Expose for outside code (e.g. list rendering)
export { ALL_CARE_PLAN_TYPES };

/** Site/House selector — fetches sites once when the dialog opens. */
function SiteSelect({ value, onChange, testid }) {
  const [sites, setSites] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/sites")
      .then((r) => { if (alive) setSites(r.data || []); })
      .catch(() => { if (alive) setSites([]); });
    return () => { alive = false; };
  }, []);
  return (
    <select
      id={testid}
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400/40"
    >
      <option value="">— Not assigned —</option>
      {sites.map((s) => (
        <option key={s.id} value={s.id}>{s.name}</option>
      ))}
    </select>
  );
}
